import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type BotRow = {
  id: number;
  name: string;
  gender: 'male' | 'female';
  personality_type: 'easy' | 'medium' | 'hard';
  avatar_path: string | null;
  wallet_balance: number;
  is_active: 0 | 1;
  games_played: number;
  matches_won: number;
  total_spent: number;
  total_won: number;
  created_at: string;
};

type Stats = {
  total: number;
  active: number;
  total_balance: number;
  lifetime_spent: number;
  lifetime_won: number;
};

type ListResp = {
  rows: BotRow[];
  total: number;
  limit: number;
  offset: number;
  stats: Stats;
};

const PAGE_SIZE = 20;

const PERSONALITY_COLOR: Record<BotRow['personality_type'], string> = {
  easy:   'bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-600/30',
  medium: 'bg-amber-600/20 text-amber-700 dark:text-amber-300 border-amber-600/30',
  hard:   'bg-red-600/20 text-red-700 dark:text-red-300 border-red-600/30',
};

const PERSONALITY_ICON: Record<BotRow['personality_type'], string> = {
  easy:   'sentiment_satisfied',
  medium: 'sentiment_neutral',
  hard:   'local_fire_department',
};

export default function BotsListPanel() {
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female'>('');
  const [personality, setPersonality] = useState<'' | BotRow['personality_type']>('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(PAGE_SIZE));
      q.set('offset', String(page * PAGE_SIZE));
      if (search.trim()) q.set('search', search.trim());
      if (gender) q.set('gender', gender);
      if (personality) q.set('personality', personality);
      if (activeOnly) q.set('active', '1');
      const d = await adminApi.get<ListResp>(`/admin/bots?${q.toString()}`);
      setData(d);
    } catch (e) {
      setErr((e as ApiError).message || 'Failed to load bots');
    } finally { setLoading(false); }
  }, [search, gender, personality, activeOnly, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [search, gender, personality, activeOnly]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70">Real wallets, personalities, stats</p>
        <button onClick={() => setCreating(true)}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
                     bg-violet-600 hover:bg-violet-500 text-white transition">
          <Icon name="add" size={16} /> New bot
        </button>
      </div>

      {/* Stats row */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatTile label="Total bots"      value={data.stats.total}           icon="smart_toy" />
          <StatTile label="Active"          value={data.stats.active}          icon="check_circle" color="text-emerald-400" />
          <StatTile label="Total balance"   value={`${data.stats.total_balance.toLocaleString('en-IN')}`}    icon="account_balance_wallet" color="text-violet-400" />
          <StatTile label="Lifetime spent"  value={`${data.stats.lifetime_spent.toLocaleString('en-IN')}`}   icon="trending_down" color="text-red-400" />
          <StatTile label="Lifetime won"    value={`${data.stats.lifetime_won.toLocaleString('en-IN')}`}     icon="trending_up" color="text-emerald-400" />
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/60" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…"
            className="w-full rounded-xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 pl-10 pr-3 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-violet-400" />
        </div>
        <select value={gender} onChange={(e) => setGender(e.target.value as typeof gender)}
          className="rounded-xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
          <option value="">All genders</option><option value="female">Female</option><option value="male">Male</option>
        </select>
        <select value={personality} onChange={(e) => setPersonality(e.target.value as typeof personality)}
          className="rounded-xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
          <option value="">All personalities</option><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-arena-700 dark:text-neon-200/75 cursor-pointer select-none">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
            className="w-4 h-4 accent-violet-500" />
          Active only
        </label>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
                                         bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 transition">
          <Icon name="refresh" size={16} /> Refresh
        </button>
      </div>

      {loading && (<div className="p-10 text-center text-arena-500 dark:text-neon-200/55"><Icon name="progress_activity" size={28} className="animate-spin inline-block" /> Loading…</div>)}
      {err && (<div className="p-4 rounded-xl bg-rose-500/10 ring-1 ring-inset ring-rose-500/30 text-red-700 dark:text-red-200 text-sm">{err}</div>)}

      {!loading && data && (
        <>
          <div className="mb-2 text-xs text-arena-600 dark:text-neon-200/60">Showing {data.rows.length} of {data.total} bots</div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
            className="rounded-2xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white dark:bg-arena-800/80 text-xs uppercase text-arena-400 dark:text-neon-200/70">
                  <tr>
                    <th className="text-left px-4 py-2.5">Bot</th>
                    <th className="text-left px-4 py-2.5">Personality</th>
                    <th className="text-right px-4 py-2.5">Wallet</th>
                    <th className="text-right px-4 py-2.5">Games</th>
                    <th className="text-right px-4 py-2.5">Win %</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((b) => {
                    const winRate = b.games_played > 0 ? Math.round((b.matches_won / b.games_played) * 100) : 0;
                    return (
                      <tr key={b.id} className="border-t ring-arena-200/60 dark:ring-arena-600/40 hover:bg-arena-50/60 dark:bg-arena-700/60/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-arena-100/60 dark:bg-arena-800 grid place-items-center overflow-hidden">
                              {b.avatar_path
                                ? <img src={b.avatar_path} alt="" className="w-full h-full object-cover" />
                                : <Icon name={b.gender === 'female' ? 'face_3' : 'face_6'} size={20} className="text-arena-600 dark:text-neon-200/60" />}
                            </div>
                            <div>
                              <div className="font-semibold text-neutral-900 dark:text-neutral-100">{b.name}</div>
                              <div className="text-[11px] text-arena-600 dark:text-neon-200/60 capitalize">{b.gender} · #{b.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${PERSONALITY_COLOR[b.personality_type]}`}>
                            <Icon name={PERSONALITY_ICON[b.personality_type]} size={12} />
                            {b.personality_type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-neutral-900 dark:text-neutral-100">
                         {b.wallet_balance.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-right text-arena-700 dark:text-neon-200/75">{b.games_played}</td>
                        <td className="px-4 py-3 text-right text-arena-700 dark:text-neon-200/75">{winRate}%</td>
                        <td className="px-4 py-3">
                          {b.is_active
                            ? <span className="text-[10px] font-bold text-emerald-400">ACTIVE</span>
                            : <span className="text-[10px] font-bold text-arena-600 dark:text-neon-200/60">INACTIVE</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/people/bots/${b.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold
                                       bg-violet-600/20 hover:bg-violet-600/40 text-violet-700 dark:text-violet-200 transition">
                            Manage <Icon name="chevron_right" size={14} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {data.rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-arena-600 dark:text-neon-200/60">No bots match your search. Use "New bot" to create one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {data.total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 disabled:opacity-40 disabled:cursor-not-allowed transition">
                <Icon name="chevron_left" size={16} /> Prev
              </button>
              <span className="text-arena-600 dark:text-neon-200/60">Page {page + 1} / {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 disabled:opacity-40 disabled:cursor-not-allowed transition">
                Next <Icon name="chevron_right" size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {creating && <CreateBotModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function StatTile({ label, value, icon, color = 'text-arena-800 dark:text-neon-200/90' }: { label: string; value: number | string; icon: string; color?: string }) {
  return (
    <div className="rounded-xl bg-white dark:bg-arena-800/60 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold">
        <Icon name={icon} size={14} className={color} /> {label}
      </div>
      <div className={`mt-1 text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function CreateBotModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'male' | 'female'>('female');
  const [personality, setPersonality] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [wallet, setWallet] = useState('1000');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await adminApi.post('/admin/bots', {
        name: name.trim(),
        gender,
        personality_type: personality,
        wallet_balance: Math.max(0, Math.round(Number(wallet) || 0)),
        is_active: true,
      });
      onCreated();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-start sm:place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-5 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <Icon name="add_circle" size={20} className="text-violet-700 dark:text-violet-300" filled /> Create bot
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-700/60">
            <Icon name="close" size={18} />
          </button>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Riya"
            className="w-full rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Gender</label>
            <div className="flex gap-2">
              {(['female', 'male'] as const).map((g) => (
                <button key={g} type="button" onClick={() => setGender(g)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition
                    ${gender === g ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]' : 'bg-arena-100/60 dark:bg-arena-800 text-arena-700 dark:text-neon-200/75 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:ring-arena-300/70 dark:hover:ring-arena-500/50'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Personality</label>
            <select value={personality} onChange={(e) => setPersonality(e.target.value as typeof personality)}
              className="w-full rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
              <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-arena-600 dark:text-neon-200/60 font-bold mb-1">Starting wallet ()</label>
          <input type="number" min={0} value={wallet} onChange={(e) => setWallet(e.target.value)}
            className="w-full rounded-lg bg-arena-100/60 dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400" />
          <p className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1">A "seed" ledger row is created for this starting amount so the balance is always traceable.</p>
        </div>
        {err && <div className="text-xs text-red-400">{err}</div>}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg bg-arena-100/60 dark:bg-arena-800 hover:bg-arena-100/60 dark:bg-arena-700/60 text-arena-800 dark:text-neon-200/90 text-sm font-semibold transition">
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()}
            className="flex-1 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-40 transition inline-flex items-center justify-center gap-2">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="check" size={14} />}
            {busy ? 'Creating…' : 'Create bot'}
          </button>
        </div>
      </form>
    </div>
  );
}
